<template>
  <div class="monthly-cash-flow-table">
    <div class="table-wrapper">
      <table class="cash-flow-table">
        <thead>
          <tr>
            <th class="field-header">Field</th>
            <th v-for="month in sortedMonths" :key="month" class="month-header">{{ month }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="header in allHeaders" :key="header">
            <td class="field-cell" :class="getFieldCellClass(header)">{{ formatFieldName(header) }}</td>
            <td v-for="month in sortedMonths" :key="month" :class="getCellClass(header)">
              {{ getCellValue(monthlyData[month], header) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MonthlyCashFlowTable',
  props: {
    monthlyData: {
      type: Object,
      required: true
    }
  },
  computed: {
    sortedMonths() {
      const months = Object.keys(this.monthlyData)
      return months.sort((a, b) => {
        // Parse month strings like "Oct-24", "Nov-24" to dates for proper sorting
        const dateA = this.parseMonthString(a)
        const dateB = this.parseMonthString(b)
        return dateA - dateB
      })
    },
    
    allHeaders() {
      const headers = new Set()
      
      // Collect all unique field names from all months
      Object.values(this.monthlyData).forEach(monthData => {
        if (monthData.Receivables) {
          Object.keys(monthData.Receivables).forEach(key => headers.add(`Receivables.${key}`))
        }
        if (monthData.Payables) {
          Object.keys(monthData.Payables).forEach(key => headers.add(`Payables.${key}`))
        }
        if (monthData.Summary_Metrics) {
          Object.keys(monthData.Summary_Metrics).forEach(key => headers.add(`Summary_Metrics.${key}`))
        }
      })
      
      return Array.from(headers).sort()
    }
  },
  methods: {
    parseMonthString(monthStr) {
      // Parse strings like "Oct-24", "Nov-24", "Jan-25" to Date objects
      const monthMap = {
        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
      }
      
      const parts = monthStr.split('-')
      if (parts.length === 2) {
        const monthName = parts[0]
        const year = parseInt('20' + parts[1]) // Convert "24" to 2024, "25" to 2025
        
        if (monthMap.hasOwnProperty(monthName) && !isNaN(year)) {
          return new Date(year, monthMap[monthName], 1)
        }
      }
      
      // Fallback: return a date far in the future if parsing fails
      return new Date(9999, 0, 1)
    },
    
    formatFieldName(key) {
      // Remove section prefix (Receivables., Payables., Summary_Metrics.)
      const fieldName = key.includes('.') ? key.split('.').slice(1).join('.') : key
      return fieldName
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },
    
    getCellValue(monthData, header) {
      const [section, field] = header.split('.')
      
      let value = null
      if (section === 'Receivables' && monthData.Receivables) {
        value = monthData.Receivables[field]
      } else if (section === 'Payables' && monthData.Payables) {
        value = monthData.Payables[field]
      } else if (section === 'Summary_Metrics' && monthData.Summary_Metrics) {
        value = monthData.Summary_Metrics[field]
      }
      
      if (value === null || value === undefined || value === '') return '-'
      
      // Format based on field type
      if (section === 'Receivables' || section === 'Payables' || 
          (section === 'Summary_Metrics' && (field.includes('Balance') || field.includes('Receivables') || field.includes('Payables')))) {
        return this.formatCurrency(value)
      }
      
      return this.formatCellValue(value)
    },
    
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '-'
      const num = typeof value === 'number' ? value : parseFloat(value)
      if (isNaN(num)) return String(value)
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num)
    },
    
    formatCellValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number') {
        if (Number.isInteger(value)) return value.toLocaleString()
        return value.toFixed(2)
      }
      return String(value)
    },
    
    getCellClass(header) {
      const [section] = header.split('.')
      if (section === 'Receivables') return 'receivables-cell'
      if (section === 'Payables') return 'payables-cell'
      if (section === 'Summary_Metrics') return 'summary-cell'
      return ''
    },
    
    getFieldCellClass(header) {
      const [section] = header.split('.')
      if (section === 'Receivables') return 'receivables-field'
      if (section === 'Payables') return 'payables-field'
      if (section === 'Summary_Metrics') return 'summary-field'
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.monthly-cash-flow-table {
  width: 100%;
}

.table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.cash-flow-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  table-layout: fixed;
  
  thead {
    background: #f8f9fa;
    
    th {
      padding: 0.75rem 1rem;
      text-align: left;
      font-weight: 600;
      color: #2d3748;
      border-bottom: 2px solid #e2e8f0;
      font-family: $brand-font-medium;
      white-space: nowrap;
      font-size: 0.8125rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      
      &.field-header {
        width: 200px;
        min-width: 200px;
        position: sticky;
        left: 0;
        background: #f8f9fa;
        z-index: 1;
      }
      
      &.month-header {
        min-width: 120px;
      }
    }
  }
  
  tbody {
    tr {
      border-bottom: 1px solid #f1f3f5;
      
      &:hover {
        background: #f8f9fa;
      }
      
      &:last-child {
        border-bottom: none;
      }
    }
    
    td {
      padding: 0.75rem 1rem;
      color: #495057;
      font-family: $brand-font-regular;
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
      
      &.field-cell {
        font-weight: 600;
        color: #2d3748;
        font-family: $brand-font-medium;
        background: #f8f9fa;
        position: sticky;
        left: 0;
        z-index: 1;
        
        &.receivables-field {
          border-left: 3px solid #059669;
        }
        
        &.payables-field {
          border-left: 3px solid #dc2626;
        }
        
        &.summary-field {
          border-left: 3px solid #6366f1;
        }
      }
      
      &.receivables-cell {
        color: #059669;
      }
      
      &.payables-cell {
        color: #dc2626;
      }
      
      &.summary-cell {
        font-weight: 500;
        color: #2d3748;
      }
    }
  }
}

@media (max-width: 768px) {
  .cash-flow-table {
    font-size: 0.75rem;
    
    th, td {
      padding: 0.5rem;
    }
  }
}
</style>

